import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { DocumentRow, operatorApi, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, DocumentStatusBadge, Empty, ErrorBox, PageHeader, Spinner, Table, Td, ago, dateTime, inputClass, num } from '../../components/ui';

const PAGE = 25;
const STATUSES = [
  ['', 'Tous les statuts'], ['draft', 'Brouillon'], ['sent', 'Envoyé'], ['in_progress', 'En cours'],
  ['completed', 'Signé'], ['declined', 'Refusé'], ['expired', 'Expiré'], ['voided', 'Annulé'],
];

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const client = params.get('client') ?? '';
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const { data, error, loading, reload } = useFetch(
    async () =>
      (await operatorApi.get<{ documents: DocumentRow[]; total: number }>('/documents', { params: { status: status || undefined, client: client || undefined, search: search || undefined, limit: PAGE, offset } })).data,
    [status, client, search, offset]
  );

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
    setOffset(0);
  };

  const total = data?.total ?? 0;

  return (
    <div>
      <PageHeader title="Documents" subtitle="Tous les documents de tous les clients" />
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input className={`${inputClass} pl-9`} placeholder="Titre, référence, client, identifiant…" value={search} onChange={(e) => { setSearch(e.target.value); setOffset(0); }} />
          </div>
          <select className={`${inputClass} !w-auto`} value={status} onChange={(e) => update('status', e.target.value)}>
            {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          {client && <Button variant="secondary" small onClick={() => update('client', '')}>Retirer le filtre client ✕</Button>}
        </div>

        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <ErrorBox message={error} onRetry={reload} />
        ) : !data || data.documents.length === 0 ? (
          <Empty>Aucun document.</Empty>
        ) : (
          <>
            <Table head={['Document', 'Client', 'Statut', 'Signatures', 'Créé', 'Terminé']}>
              {data.documents.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/documents/${d.id}`)}>
                  <Td>
                    <span className="font-medium text-slate-900">{d.title}</span>
                    <div className="font-mono text-xs text-slate-400">{d.id.slice(0, 8)}</div>
                  </Td>
                  <Td><Link className="hover:text-indigo-600" to={`/clients/${d.client_id}`} onClick={(e) => e.stopPropagation()}>{d.client}</Link></Td>
                  <Td><DocumentStatusBadge status={d.status} /></Td>
                  <Td>
                    <Badge tone={d.signers_signed === d.signers_total && d.signers_total > 0 ? 'green' : 'slate'}>{d.signers_signed} / {d.signers_total}</Badge>
                  </Td>
                  <Td className="text-slate-500" >{ago(d.created_at)}</Td>
                  <Td className="whitespace-nowrap text-slate-500">{dateTime(d.completed_at)}</Td>
                </tr>
              ))}
            </Table>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>{num(offset + 1)}–{num(Math.min(offset + PAGE, total))} sur {num(total)}</span>
              <div className="flex gap-2">
                <Button variant="secondary" small disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="secondary" small disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
